import { getStrapiToken } from '@/services/auth/getStrapiToken';
// @/services/app/store/modules/ca-supplier-input.js

/**
 * SupplierInputHandler
 * 
 * Manages supplier input data for carbon accounting:
 * - API integration for retrieving and manipulating supplier data
 * - Data transformation and grouping by year via distribution relationships
 * - CRUD operations with validation and error handling
 * - Real-time updates via WebSocket connection
 * - Orphaned data cleanup and relationship management
 * - Integration with emissions recalculation workflow
 */
export class SupplierInputHandler {
  /**
   * Initialize the supplier input handler with a store reference
   * @param {Object} store - The main application store instance
   */
  constructor(store) {
    this.store = store;
    
    // Register with WebSocketHandler if available
    if (this.store.webSocketHandler) {
      this.store.webSocketHandler.registerHandler(
        'SupplierInputHandler',
        this.handleUpdate.bind(this),
        [
          'supplier-input', 
          'supplier-input:update', 
          'supplier-input:create', 
          'supplier-input:delete'
        ],
        this
      );
    }
  }

  /**
   * Load all supplier input data from API
   * @returns {Promise<Object>} Supplier input data grouped by year
   * @throws {Error} If API request fails
   */
  async loadSupplierInput() {
    try {
      this.store.state.loading.supplierInput = true;
      const projectId = this.store.getProjectId();

      // Early return if no project ID is available
      if (!projectId) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Cannot load supplier input: No project ID available');
        }
        return {};
      }

      // Use new project route
      const url = `${process.env.GATSBY_STRAPI_URL}/api/ca-supplier-inputs/project/${projectId}`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${getStrapiToken()}`
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch supplier input data: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const { data: rawData } = await response.json();

      // Group by year using spend distribution info
      const byYear = this.groupSupplierInputsByYear(rawData);

      // Update store with simplified metadata
      this.store.state.data.supplierInput = {
        byYear,
        meta: {
          lastSync: new Date().toISOString()
        }
      };

      this.store.notifySubscribers('supplierInput');
      await this.store.saveState();
      return byYear;

    } catch (error) {
      console.error('Failed to load supplier data:', error);
      this.store.handleError('supplierInput', error);
      return {};
    } finally {
      this.store.state.loading.supplierInput = false;
    }
  }

  /**
   * Group supplier inputs by year using spend distribution data
   * @private
   * @param {Array} supplierInputs - Raw supplier input data
   * @returns {Object} Supplier inputs grouped by year
   */
  groupSupplierInputsByYear(supplierInputs) {
    if (!Array.isArray(supplierInputs) || supplierInputs.length === 0) {
      return {};
    }

    const byYear = {};
    const unmappedInputs = []; // Collect unmapped inputs for logging

    // Get spend distribution data for mapping
    const spendDistByYear = this.store.state.data.spendDistribution?.byYear || {};

    // First, create a map of distributionId to year
    const distributionYearMap = {};
    Object.entries(spendDistByYear).forEach(([year, distributions]) => {
      distributions.forEach(dist => {
        if (dist.distributionId) {
          distributionYearMap[dist.distributionId] = year;
        }
      });
    });

    // Now assign supplier inputs to years
    supplierInputs.forEach(item => {
      const data = item.attributes || item;
      const distributionId = data.DistributionId;

      if (!distributionId) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Supplier input missing distribution ID:', item);
        }
        unmappedInputs.push(item);
        return;
      }

      // Find year from distribution mapping
      const year = distributionYearMap[distributionId];

      if (!year) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(`Supplier input with distribution ID ${distributionId} couldn't be mapped to a year:`, item);
        }
        unmappedInputs.push(item);
        return;
      }

      if (!byYear[year]) {
        byYear[year] = [];
      }

      byYear[year].push(this.transformSupplierInput(item));
    });

    // Log unmapped inputs for debugging
    if (unmappedInputs.length > 0 && process.env.NODE_ENV === 'development') {
      console.warn(`Found ${unmappedInputs.length} supplier inputs that couldn't be mapped to a year.`);
    }

    return byYear;
  }

  /**
   * Transform individual supplier input entry
   * @private
   * @param {Object} item - Raw supplier input item
   * @returns {Object} Transformed supplier input data
   */
  transformSupplierInput(item) {
    if (!item) return null;

    const data = item.attributes || item;

    return {
      id: item.id,
      distributionId: data.DistributionId,
      supplierInputId: data.SupplierInputId,
      supplierId: data.SupplierId,
      supplierName: data.SupplierName || '',
      countryCode: data.CountryCode || '',
      shareOfSectorInput: parseFloatSafe(data.ShareOfSectorInput),
      snowflakeId: data.SnowflakeId || ''
    };
  }

  /**
   * Create a new supplier input
   * @param {Object} data - Supplier input data
   * @param {string} data.DistributionId - Distribution ID to link with
   * @param {string} data.SupplierId - Supplier ID
   * @param {string} [data.SupplierName] - Supplier name
   * @param {string} [data.CountryCode] - Country code
   * @param {string|number} [data.ShareOfSectorInput] - Share of sector input percentage
   * @param {string} [data.SupplierInputId] - Optional ID (generated if not provided)
   * @returns {Promise<boolean>} Success status
   * @throws {Error} If validation fails or API request fails
   */
  async createSupplierInput(data) {
    try {
      const projectId = this.store.getProjectId();

      // Validate inputs
      if (!projectId) {
        throw new Error('Project ID is required to create supplier input');
      }

      if (!data.DistributionId) {
        throw new Error('Distribution ID is required');
      }

      if (!data.SupplierId) {
        throw new Error('Supplier ID is required');
      }

      // Validate shareOfSectorInput if provided
      if (data.ShareOfSectorInput !== undefined) {
        const shareOfInput = parseFloatSafe(data.ShareOfSectorInput);
        if (shareOfInput < 0 || shareOfInput > 100) {
          throw new Error('Share of sector input must be between 0 and 100 percent');
        }
      }

      // Get year information for this distribution
      let year = null;
      try {
        // Find the year for this distribution ID
        const allDistributionsByYear = this.store.state.data.spendDistribution?.byYear || {};
        for (const [yearKey, distributions] of Object.entries(allDistributionsByYear)) {
          const found = distributions.find(d => d.distributionId === data.DistributionId);
          if (found) {
            year = yearKey;
            break;
          }
        }
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Error finding year for distribution:', err);
        }
      }

      // 1. Create in Strapi
      const response = await fetch(
        `${process.env.GATSBY_STRAPI_URL}/api/ca-supplier-inputs/project/${projectId}/create`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getStrapiToken()}`
          },
          body: JSON.stringify({
            data: {
              DistributionId: data.DistributionId,
              SupplierId: data.SupplierId,
              SupplierName: data.SupplierName || '',
              CountryCode: data.CountryCode || '',
              ShareOfSectorInput: data.ShareOfSectorInput?.toString() || '0',
              SupplierInputId: data.SupplierInputId || crypto.randomUUID()
            }
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || `Failed to create supplier input: ${response.statusText}`);
      }

      const result = await response.json();

      // 2. Sync to Snowflake
      const syncResponse = await fetch(
        `${process.env.GATSBY_STRAPI_URL}/api/ca-supplier-inputs/project/${projectId}/${result.data.id}/sync`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${getStrapiToken()}`
          }
        }
      );

      if (!syncResponse.ok) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Supplier input created but sync to Snowflake failed. Will retry on next calculation.');
        }
      }

      // 3. Flag for recalculation using shared utility
      if (year) {
        await this.store.emissionsHandler.flagYearForRecalculation(year);
      }

      // 4. Reload data
      await this.loadSupplierInput();

      // 5. Notify emissions handler
      if (year) {
        this.store.emissionsHandler.handleUpdate({
          action: 'create',
          method: 'createSupplierInput',
          year: year,
          timestamp: new Date().toISOString(),
          source: 'direct',
          scope: 'scope3',
          details: {
            id: result.data.id,
            supplierId: data.SupplierId,
            distributionId: data.DistributionId
          }
        });
      }

      return true;

    } catch (error) {
      console.error('Failed to create supplier input:', error);
      this.store.handleError('supplierInput', error);
      throw error;
    }
  }

  /**
   * Update an existing supplier input
   * @param {string} documentId - Document ID to update
   * @param {Object} updates - Updates to apply
   * @param {string|number} [updates.ShareOfSectorInput] - Share of sector input percentage
   * @param {string} [updates.SupplierName] - Supplier name
   * @param {string} [updates.CountryCode] - Country code
   * @returns {Promise<boolean>} Success status
   * @throws {Error} If validation fails or API request fails
   */
  async updateSupplierInput(documentId, updates) {
    try {
      const projectId = this.store.getProjectId();

      // Validate inputs
      if (!projectId) {
        throw new Error('Project ID is required to update supplier input');
      }

      if (!documentId) {
        throw new Error('Document ID is required');
      }

      // Validate shareOfSectorInput if provided
      if (updates.ShareOfSectorInput !== undefined) {
        const shareOfInput = parseFloatSafe(updates.ShareOfSectorInput);
        if (shareOfInput < 0 || shareOfInput > 100) {
          throw new Error('Share of sector input must be between 0 and 100 percent');
        }

        // Ensure it's a string for consistency
        updates.ShareOfSectorInput = shareOfInput.toString();
      }

      // Get information about this supplier input
      let year = null;
      try {
        // Find the record in the current state to get distribution ID
        const allYears = this.store.state.data.supplierInput?.byYear || {};
        for (const [yearKey, suppliers] of Object.entries(allYears)) {
          const found = suppliers.find(s => s.id === documentId);
          if (found) {
            year = yearKey;
            break;
          }
        }
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Error finding metadata for supplier input:', err);
        }
      }

      // 1. Update in Strapi
      const response = await fetch(
        `${process.env.GATSBY_STRAPI_URL}/api/ca-supplier-inputs/project/${projectId}/${documentId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getStrapiToken()}`
          },
          body: JSON.stringify({
            data: updates
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || `Failed to update supplier input: ${response.statusText}`);
      }

      // 2. Sync to Snowflake
      const syncResponse = await fetch(
        `${process.env.GATSBY_STRAPI_URL}/api/ca-supplier-inputs/project/${projectId}/${documentId}/sync`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${getStrapiToken()}`
          }
        }
      );

      if (!syncResponse.ok) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Supplier input updated but sync to Snowflake failed. Will retry on next calculation.');
        }
      }

      // 3. Flag for recalculation using shared utility
      if (year) {
        await this.store.emissionsHandler.flagYearForRecalculation(year);
      }

      // 4. Reload data
      await this.loadSupplierInput();

      // 5. Notify emissions handler
      if (year) {
        this.store.emissionsHandler.handleUpdate({
          action: 'update',
          method: 'updateSupplierInput',
          year: year,
          timestamp: new Date().toISOString(),
          source: 'direct',
          scope: 'scope3',
          details: {
            id: documentId,
            updates: updates
          }
        });
      }

      return true;

    } catch (error) {
      console.error('Failed to update supplier input:', error);
      this.store.handleError('supplierInput', error);
      throw error;
    }
  }

  /**
   * Delete a supplier input
   * @param {Object} supplier - Supplier object with ID to delete
   * @param {string} supplier.id - ID of the supplier input to delete
   * @returns {Promise<boolean>} Success status
   * @throws {Error} If validation fails or API request fails
   */
  async deleteSupplierInput(supplier) {
    try {
      const projectId = this.store.getProjectId();

      // Validate inputs
      if (!projectId) {
        throw new Error('Project ID is required to delete supplier input');
      }

      if (!supplier || !supplier.id) {
        throw new Error('Supplier ID is required');
      }

      // Get year information and distribution ID
      let year = null;
      let supplierDetails = null;

      try {
        // Find the record to get year and distribution ID
        const allYears = this.store.state.data.supplierInput?.byYear || {};
        for (const [yearKey, suppliers] of Object.entries(allYears)) {
          const found = suppliers.find(s => s.id === supplier.id);
          if (found) {
            year = yearKey;
            supplierDetails = found;
            break;
          }
        }
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Error finding metadata for supplier deletion:', err);
        }
      }

      // 1. Delete from Strapi
      const response = await fetch(
        `${process.env.GATSBY_STRAPI_URL}/api/ca-supplier-inputs/project/${projectId}/${supplier.id}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${getStrapiToken()}`
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || `Failed to delete supplier input: ${response.statusText}`);
      }

      // Individual deletion already handled Snowflake cleanup - no additional sync needed
      if (process.env.NODE_ENV === 'development') {
        console.log('Individual supplier deletion completed - Snowflake already updated by backend');
      }

      // 3. Flag for recalculation using shared utility
      if (year) {
        await this.store.emissionsHandler.flagYearForRecalculation(year);
      }

      // 4. Reload data
      await this.loadSupplierInput();

      // 5. Notify emissions handler
      if (year) {
        this.store.emissionsHandler.handleUpdate({
          action: 'delete',
          method: 'deleteSupplierInput',
          year: year,
          id: supplier.id,
          timestamp: new Date().toISOString(),
          source: 'direct',
          scope: 'scope3',
          details: supplierDetails
        });
      }

      return true;

    } catch (error) {
      console.error('Failed to delete supplier input:', error);
      this.store.handleError('supplierInput', error);
      throw error;
    }
  }

  /**
   * Handle real-time supplier input updates from WebSocket
   * @param {Object} data - Update data from WebSocket
   * @returns {Promise<void>}
   */
  async handleUpdate(data) {
    try {
      // Simply reload the data
      await this.loadSupplierInput();

      // Try to extract year information from the data if available
      let year = null;
      if (data.data && data.data.distributionId) {
        try {
          // Use the distribution ID to find the year
          const distId = data.data.distributionId;
          const spendDistByYear = this.store.state.data.spendDistribution?.byYear || {};
          for (const [yearKey, distributions] of Object.entries(spendDistByYear)) {
            if (distributions.some(d => d.distributionId === distId)) {
              year = yearKey;
              break;
            }
          }
        } catch (err) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('Error extracting year from websocket data:', err);
          }
        }
      }

      // Flag for recalculation using shared utility if we have a year
      if (year) {
        await this.store.emissionsHandler.flagYearForRecalculation(year);
      }

      // Forward the update to the emissions handler
      if (year) {
        this.store.emissionsHandler.handleUpdate({
          action: data.action || 'update',
          method: 'websocket',
          year: year,
          timestamp: new Date().toISOString(),
          source: 'websocket',
          scope: 'scope3',
          details: data
        });
      }

      this.store.notifySubscribers('syncStatus', {
        type: 'success',
        message: year
          ? `Supplier input data for year ${year} updated successfully`
          : 'Supplier input data updated successfully',
        timestamp: new Date(),
        details: {
          updatedAt: new Date().toISOString(),
          source: 'websocket',
          year: year
        }
      });

    } catch (error) {
      console.error('Failed to reload supplier input data:', error);
      this.store.handleError('supplierInput', {
        message: error.message,
        details: {
          timestamp: new Date().toISOString(),
          source: 'websocket',
          originalError: error
        }
      });
    }
  }

  /**
   * Get supplier input data for specific year
   * @param {string|number} year - Year to get data for
   * @returns {Array|null} Supplier input data or null if not loaded
   */
  getSupplierInputData(year) {
    return this.store.state.data.supplierInput.byYear[year] || null;
  }

  /**
   * Check if supplier input data exists for year
   * @param {string|number} year - Year to check
   * @returns {boolean} True if data exists
   */
  hasSupplierInputData(year) {
    return !!this.store.state.data.supplierInput.byYear[year];
  }

  /**
   * Get all available years with supplier input data
   * @returns {Array<string>} Array of years sorted in descending order
   */
  getAvailableYears() {
    return Object.keys(this.store.state.data.supplierInput.byYear)
      .filter(year => year !== 'unassigned')
      .sort((a, b) => b.localeCompare(a));
  }

  /**
   * Clean up orphaned supplier inputs that can't be mapped to a year
   * This can be called periodically or when issues are detected
   * @returns {Promise<Object>} Status of the cleanup operation
   */
  async cleanupOrphanedSupplierInputs() {
    try {
      const projectId = this.store.getProjectId();

      if (!projectId) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Cannot cleanup supplier inputs: No project ID available');
        }
        return { success: false, message: 'No project ID available' };
      }

      // Load all supplier inputs
      const response = await fetch(
        `${process.env.GATSBY_STRAPI_URL}/api/ca-supplier-inputs/project/${projectId}`,
        {
          headers: {
            Authorization: `Bearer ${getStrapiToken()}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch supplier inputs: ${response.status} ${response.statusText}`);
      }

      const { data: allSupplierInputs } = await response.json();

      // Get spend distribution data for mapping
      const spendDistByYear = this.store.state.data.spendDistribution?.byYear || {};

      // Create a map of valid distribution IDs
      const validDistributionIds = new Set();
      Object.values(spendDistByYear).forEach(distributions => {
        distributions.forEach(dist => {
          if (dist.distributionId) {
            validDistributionIds.add(dist.distributionId);
          }
        });
      });

      // Find orphaned supplier inputs
      const orphanedInputs = allSupplierInputs.filter(item => {
        const data = item.attributes || item;
        const distributionId = data.DistributionId;
        return distributionId && !validDistributionIds.has(distributionId);
      });

      if (orphanedInputs.length === 0) {
        if (process.env.NODE_ENV === 'development') {
          console.log('No orphaned supplier inputs found');
        }
        return { success: true, message: 'No orphaned supplier inputs found' };
      }

      if (process.env.NODE_ENV === 'development') {
        console.log(`Found ${orphanedInputs.length} orphaned supplier inputs to delete`);
      }

      // Delete each orphaned input
      const deleteResults = await Promise.all(
        orphanedInputs.map(item =>
          fetch(
            `${process.env.GATSBY_STRAPI_URL}/api/ca-supplier-inputs/project/${projectId}/${item.id}`,
            {
              method: 'DELETE',
              headers: {
                Authorization: `Bearer ${getStrapiToken()}`
              }
            }
          )
        )
      );

      // Count successful deletions
      const successCount = deleteResults.filter(res => res.ok).length;

      // Reload supplier data to reflect changes
      await this.loadSupplierInput();

      return {
        success: true,
        message: `Deleted ${successCount} of ${orphanedInputs.length} orphaned supplier inputs`,
        deleted: successCount,
        total: orphanedInputs.length
      };

    } catch (error) {
      console.error('Failed to cleanup orphaned supplier inputs:', error);
      this.store.handleError('supplierInput', error);
      return {
        success: false,
        message: `Error cleaning up orphaned supplier inputs: ${error.message}`,
        error
      };
    }
  }
}

/**
 * Safely parse float with fallback to 0
 * @param {string|number} value - Value to parse
 * @returns {number} Parsed float or 0 if invalid
 */
function parseFloatSafe(value) {
  if (value === null || value === undefined) return 0;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? 0 : parsed;
}